'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import { HiOutlineDocument } from '@react-icons/all-files/hi/HiOutlineDocument';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFormGroup,
  StyledCalculateButton,
  StyledResult,
  StyledAnchorTable,
  StyledDistributionBar,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
  StyledCtaBanner,
} from './AnchorTextAnalyzerTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
}

type AnchorType = 'exact-match' | 'partial-match' | 'branded' | 'generic' | 'naked-url' | 'image';

interface IAnchorEntry {
  text: string;
  href: string;
  type: AnchorType;
}

interface ITypeCount {
  type: AnchorType;
  label: string;
  count: number;
  percent: number;
  color: string;
}

interface IToolResult {
  anchors: IAnchorEntry[];
  typeCounts: ITypeCount[];
  totalLinks: number;
  exactMatchPercent: number;
  status: 'poor' | 'fair' | 'good';
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

const TYPE_META: Record<AnchorType, { label: string; color: string }> = {
  'exact-match': { label: 'Exact Match', color: '#4c4fff' },
  'partial-match': { label: 'Partial Match', color: '#8b5cf6' },
  'branded': { label: 'Branded', color: '#16a34a' },
  'generic': { label: 'Generic', color: '#b45309' },
  'naked-url': { label: 'Naked URL', color: '#0ea5e9' },
  'image': { label: 'Image', color: '#6b7280' },
};

const GENERIC_ANCHORS = new Set([
  'click here', 'here', 'read more', 'learn more', 'more', 'link', 'this', 'this page',
  'this article', 'this post', 'visit', 'visit here', 'go here', 'source', 'see more',
  'find out more', 'view', 'download', 'get it', 'try it', 'start', 'continue',
]);

function classifyAnchor(text: string, href: string, keyword: string, brand: string): AnchorType {
  const t = text.trim().toLowerCase();

  if (!t || t === '') return 'image';
  if (/^https?:\/\//.test(t) || /^www\./.test(t)) return 'naked-url';
  if (GENERIC_ANCHORS.has(t)) return 'generic';
  if (brand && t.includes(brand.toLowerCase())) return 'branded';
  if (keyword && t === keyword.toLowerCase()) return 'exact-match';
  if (keyword && t.includes(keyword.toLowerCase())) return 'partial-match';
  return 'generic';
}

function extractAnchors(html: string, keyword: string, brand: string): IAnchorEntry[] {
  const anchorRegex = /<a\s[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi;
  const results: IAnchorEntry[] = [];
  let match;

  while ((match = anchorRegex.exec(html)) !== null) {
    const href = match[1];
    const innerHtml = match[2];
    // Strip inner HTML tags to get text
    const text = innerHtml.replace(/<[^>]+>/g, '').trim();
    const hasImg = /<img/i.test(innerHtml);
    const type = hasImg && !text ? 'image' : classifyAnchor(text, href, keyword, brand);
    results.push({ text: text || (hasImg ? '[image]' : ''), href, type });
  }

  return results;
}

function getWidgetDescription(slug: string): string {
  switch (slug) {
    case 'accordion':
      return 'Use accordions to add contextually rich content sections with descriptive internal links. Natural anchor text thrives in well-structured, readable content formats.';
    case 'tabs':
      return 'Tabs let you organize content with keyword-rich headings and links. Each tab section is a natural anchor text opportunity that avoids over-optimization on a single page.';
    case 'comparison-tables':
      return 'Comparison tables create natural link contexts using product names, features, and descriptive phrases rather than generic "click here" anchors.';
    case 'feeds':
      return 'Content feeds display recent articles with their titles as anchor text, naturally building descriptive, varied link text across your site.';
    default:
      return 'Build better internal link structures with content widgets that create natural anchor text opportunities.';
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'accordion': return 'Add Accordion Free →';
    case 'tabs': return 'Add Tabs Free →';
    case 'comparison-tables': return 'Add Tables Free →';
    case 'feeds': return 'Add Feeds Free →';
    default: return 'Try It Free →';
  }
}

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'keyword-density-checker': <HiOutlineSearch />,
  'canonical-url-checker': <HiOutlineDocument />,
  'xml-sitemap-validator': <HiOutlineDocument />,
};

export const AnchorTextAnalyzerTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [html, setHtml] = useState('');
  const [keyword, setKeyword] = useState('');
  const [brand, setBrand] = useState('');
  const [result, setResult] = useState<IToolResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);

  const handleAnalyze = () => {
    if (!html.trim()) {
      setError('Please paste your HTML content.');
      return;
    }
    setError('');

    const anchors = extractAnchors(html.trim(), keyword.trim(), brand.trim());

    if (anchors.length === 0) {
      setError('No anchor tags found in the provided HTML. Make sure you paste valid HTML with <a> elements.');
      return;
    }

    const total = anchors.length;
    const counts: Record<AnchorType, number> = {
      'exact-match': 0, 'partial-match': 0, 'branded': 0,
      'generic': 0, 'naked-url': 0, 'image': 0,
    };
    for (const a of anchors) counts[a.type]++;

    const typeCounts: ITypeCount[] = (Object.keys(counts) as AnchorType[]).map((type) => ({
      type,
      label: TYPE_META[type].label,
      count: counts[type],
      percent: Math.round((counts[type] / total) * 100),
      color: TYPE_META[type].color,
    })).filter((t) => t.count > 0);

    const exactMatchPercent = Math.round((counts['exact-match'] / total) * 100);
    const status: 'poor' | 'fair' | 'good' =
      exactMatchPercent > 30 ? 'poor' : exactMatchPercent > 15 ? 'fair' : 'good';

    setResult({ anchors, typeCounts, totalLinks: total, exactMatchPercent, status });
    setHasCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const statusLabel =
    result?.status === 'good' ? 'Balanced' : result?.status === 'fair' ? 'Watch Exact Match' : 'Over-Optimized';

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">Anchor Text Analyzer</span></h1>
        <p>
          Paste your HTML and instantly see anchor text type distribution, exact match
          percentage, and over-optimization risk. Free, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Analyze Your Anchor Text Distribution</h2>

          <StyledFormGroup>
            <label htmlFor="htmlContent">HTML Content</label>
            <textarea
              id="htmlContent"
              placeholder={'<p>Check out our <a href="/seo-guide">SEO guide</a> or <a href="/contact">contact us</a>.</p>'}
              value={html}
              onChange={(e) => setHtml(e.target.value)}
            />
            <p className="hint">Paste the full HTML of your page or a specific content section. All {'<a>'} tags will be extracted.</p>
          </StyledFormGroup>

          <StyledFormGroup>
            <label htmlFor="keyword">Target Keyword (optional)</label>
            <input
              id="keyword"
              type="text"
              placeholder="e.g. SEO guide"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <p className="hint">Helps identify exact match and partial match anchors in the results</p>
          </StyledFormGroup>

          <StyledFormGroup>
            <label htmlFor="brand">Brand Name (optional)</label>
            <input
              id="brand"
              type="text"
              placeholder="e.g. CommonNinja"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            />
            <p className="hint">Helps identify branded anchor text vs. generic or keyword-rich anchors</p>
          </StyledFormGroup>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleAnalyze}>
            Analyze Anchor Text
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <p className="result-label">Anchor Text Profile</p>
              <p className="result-number">{statusLabel}</p>
              <span className="result-badge">
                {result.totalLinks} links | {result.exactMatchPercent}% exact match
              </span>

              <div className="result-benchmark">
                <p>
                  <strong>{result.totalLinks}</strong> anchor tag{result.totalLinks !== 1 ? 's' : ''} found.
                  {result.exactMatchPercent > 30 && (
                    <> Exact match at <strong style={{ color: '#dc2626' }}>{result.exactMatchPercent}%</strong> exceeds the 30% risk threshold.</>
                  )}
                  {result.exactMatchPercent <= 15 && (
                    <> Exact match at <strong style={{ color: '#16a34a' }}>{result.exactMatchPercent}%</strong> is within safe range.</>
                  )}
                  {result.exactMatchPercent > 15 && result.exactMatchPercent <= 30 && (
                    <> Exact match at <strong style={{ color: '#b45309' }}>{result.exactMatchPercent}%</strong> is approaching risk levels. Monitor carefully.</>
                  )}
                </p>
              </div>

              {result.typeCounts.length > 0 && (
                <>
                  {result.typeCounts.map((tc) => (
                    <StyledDistributionBar key={tc.type}>
                      <div className="bar-header">
                        <span className="bar-label" style={{ color: tc.color }}>{tc.label}</span>
                        <span className="bar-count">{tc.count} ({tc.percent}%)</span>
                      </div>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${tc.percent}%`, background: tc.color }} />
                      </div>
                    </StyledDistributionBar>
                  ))}
                </>
              )}

              {result.status === 'poor' && (
                <div className="result-pain">
                  <strong>Over-optimization detected:</strong> Exact match anchor text above 30%
                  is a well-documented risk factor for Google manual actions on link profiles.
                  Replace some exact match anchors with branded, partial match, or generic alternatives
                  to bring the distribution back to a natural range.
                </div>
              )}

              {result.status === 'fair' && (
                <div className="result-pain">
                  <strong>Approaching risk threshold:</strong> Your exact match percentage is
                  elevated but not yet in the danger zone. Diversify anchor text going forward
                  by using branded and partial match variations to keep the profile natural.
                </div>
              )}

              {result.status === 'good' && (
                <div className="result-pain">
                  <strong>Balanced profile:</strong> Your anchor text distribution looks natural.
                  Continue using a mix of branded, generic, and descriptive anchors to maintain
                  a healthy link profile that avoids over-optimization signals.
                </div>
              )}

              {result.anchors.length > 0 && (
                <StyledAnchorTable>
                  <h3>All Extracted Anchors</h3>
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Anchor Text</th>
                          <th>Type</th>
                          <th>Destination</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.anchors.slice(0, 50).map((a, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td className="anchor-text">{a.text || '(empty)'}</td>
                            <td>
                              <span className="anchor-badge" style={{ background: `${TYPE_META[a.type].color}18`, color: TYPE_META[a.type].color }}>
                                {TYPE_META[a.type].label}
                              </span>
                            </td>
                            <td className="anchor-href">{a.href.slice(0, 50)}{a.href.length > 50 ? '...' : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {result.anchors.length > 50 && (
                    <p className="table-note">Showing first 50 of {result.anchors.length} anchors.</p>
                  )}
                </StyledAnchorTable>
              )}
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Build Natural Internal Link Structures</h2>
              <p className="subtitle">
                These widgets help you create content that generates diverse, natural anchor text automatically.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free SEO Tools</h2>
            <p className="subtitle">Other free tools to help you audit and optimize your site.</p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/seo/${tool.slug}`}
                >
                  <span className="tool-icon">{TOOL_ICONS[tool.slug] || <HiOutlineSearch />}</span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool →</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}

      </StyledToolBody>
    </>
  );
};
